import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getKRIs, RISK_CATEGORIES } from '@/services/risk';
import { toast } from 'sonner';

const RiskKRIsPage = () => {
  const [kris, setKris] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKRIs();
  }, []);

  const loadKRIs = async () => {
    try {
      setLoading(true);
      const data = await getKRIs();
      setKris(data.kris || []);
    } catch (error) {
      toast.error('Failed to load KRIs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      green: 'bg-green-100 border-green-500 text-green-700',
      amber: 'bg-yellow-100 border-yellow-500 text-yellow-700',
      red: 'bg-red-100 border-red-500 text-red-700'
    };
    return colors[status] || colors.green;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingDown className="w-4 h-4 text-green-500" />;
      case 'declining': return <TrendingUp className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Key Risk Indicators</h1>
          <p className="text-gray-500 mt-1">Monitor risk metrics and thresholds</p>
        </div>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          Add KRI
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Within Tolerance</p>
                <p className="text-3xl font-bold text-green-600">
                  {kris.filter(k => k.status === 'green').length}
                </p>
              </div>
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Warning</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {kris.filter(k => k.status === 'amber').length}
                </p>
              </div>
              <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Breached</p>
                <p className="text-3xl font-bold text-red-600">
                  {kris.filter(k => k.status === 'red').length}
                </p>
              </div>
              <div className="w-4 h-4 rounded-full bg-red-500"></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {kris.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No KRIs Defined</h3>
            <p className="text-gray-500 mb-4">Create key risk indicators to monitor your risk metrics</p>
            <Button><Plus className="w-4 h-4 mr-2" /> Create First KRI</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kris.map(kri => (
            <Card key={kri.id} className={`border-l-4 ${getStatusColor(kri.status)}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{kri.name}</CardTitle>
                  {getTrendIcon(kri.trend)}
                </div>
                <p className="text-sm text-gray-500">{kri.kri_id}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold">{kri.current_value}</p>
                    <p className="text-sm text-gray-500">{kri.metric_type}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Green threshold</span>
                      <span className="font-medium">≤ {kri.green_threshold}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Amber threshold</span>
                      <span className="font-medium">≤ {kri.amber_threshold}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Red threshold</span>
                      <span className="font-medium">> {kri.red_threshold}</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-xs text-gray-500">Owner: {kri.owner}</p>
                    <p className="text-xs text-gray-500">Frequency: {kri.collection_frequency}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RiskKRIsPage;